import { WaterfallDistributionData } from "gen/model/waterfallDistributionData";
import { WaterfallTierTypeEnum } from "gen/model/waterfallTierTypeEnum";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { ClickableElementGenericLinkInfo } from "shared/components/clickable/ClickableElementLinkInfo";
import { DataTableCellLink } from "shared/components/table/data/DataTableCellLink";
import { defaultDataTableRenderCellValue } from "shared/components/table/data/DataTableCellValueRenderer";
import { DataTableProps } from "shared/components/table/data/DataTableProps";
import { RenderCellValueFn } from "shared/components/table/data/DataTableTypes";
import { rootRoute } from "shared/navigation/Routes";

export interface useWaterfallDistributionTableProps {
  fundFamilyId: string;
  fundFamilyEventId: string;
  fundFamilySubEventId: string;
  fundEntityId: string;
  data?: WaterfallDistributionData;
  investorEntityId?: string;
}

export const useWaterfallDistributionTable = ({
  fundFamilyId,
  fundFamilyEventId,
  fundFamilySubEventId,
  fundEntityId,
  data,
  investorEntityId,
}: useWaterfallDistributionTableProps): DataTableProps => {
  const { t } = useTranslation(["common", "distribution"]);

  const renderCellValue = useCallback<RenderCellValueFn>(
    (props, fieldDisabled, params) => {
      const baseRoute = rootRoute({})
        .fundFamilies({})
        .fundFamily({ fundFamilyId })
        .capitalActivity({})
        .capitalActivityEvent({ fundFamilyEventId })
        .waterfall({});
      const renderedValue = defaultDataTableRenderCellValue(
        props,
        fieldDisabled,
        params,
      );
      const { cell } = props;
      if (
        cell.metadata?.type == "waterfall_tier" &&
        cell.metadata.tier_type == WaterfallTierTypeEnum.preferred_return &&
        cell.metadata.waterfall_tier_mapping_id
      ) {
        const linkInfo: ClickableElementGenericLinkInfo = {
          type: "generic",
          variant: "tableCellAction",
          to: investorEntityId
            ? baseRoute
                .review({ fundFamilySubEventId })
                .breakdown({ fundEntityId, investorEntityId })
                .investorPreferredReturn({}).$
            : baseRoute.review({ fundFamilySubEventId }).fundPreferredReturn({
                fundEntityId,
              }).$,
        };
        return (
          <>
            {renderedValue}
            <DataTableCellLink
              tableContext={props.tableContext}
              columnContext={props.columnContext}
              linkInfo={linkInfo}
              onButtonClick={undefined}
              row={props.row}
            >
              {t("common:viewDetails")}
            </DataTableCellLink>
          </>
        );
      }
      return renderedValue;
    },
    [
      fundFamilyEventId,
      fundFamilyId,
      fundFamilySubEventId,
      fundEntityId,
      investorEntityId,
      t,
    ],
  );

  return { data: data!.waterfall_results_table, renderCellValue };
};
